import { useState, useEffect } from 'react';
import { Clock, Calendar, FileText, Receipt, Flag, Image, File, User, FolderOpen, CheckCircle } from '../../icons/wp-icons';
import { getProjektTimeline } from '../../api/projektApi';

const ICON_MAP = {
  projekt: FolderOpen,
  anfrage: FileText,
  termin: Calendar,
  kva: FileText,
  rechnung: Receipt,
  meilenstein: Flag,
  zeiterfassung: Clock,
  foto: Image,
  dokument: File
};

const COLOR_MAP = {
  projekt: 'bg-primary text-white',
  anfrage: 'bg-blue-100 text-blue-600',
  termin: 'bg-purple-100 text-purple-600',
  kva: 'bg-yellow-100 text-yellow-700',
  rechnung: 'bg-green-100 text-green-600',
  meilenstein: 'bg-orange-100 text-orange-600',
  zeiterfassung: 'bg-gray-100 text-gray-600',
  foto: 'bg-pink-100 text-pink-600',
  dokument: 'bg-indigo-100 text-indigo-600'
};

export default function ProjektTimeline({ projektId }) {
  const [timeline, setTimeline] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle');

  useEffect(() => {
    loadTimeline();
  }, [projektId]);

  const loadTimeline = async () => {
    try {
      const data = await getProjektTimeline(projektId);
      setTimeline(data || []);
    } catch (error) {
      console.error('Fehler beim Laden der Timeline:', error);
      setTimeline([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    const d = new Date(date);
    const now = new Date();
    const diff = now - d;
    const days = Math.floor(diff / (1000 * 60 * 60 * 24));

    if (days === 0) return 'Heute';
    if (days === 1) return 'Gestern';
    if (days < 7) return `Vor ${days} Tagen`;

    return d.toLocaleDateString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString('de-DE', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredTimeline = filter === 'alle'
    ? timeline
    : timeline.filter(item => item.typ === filter);

  // Gruppiere nach Datum
  const groupedByDate = filteredTimeline.reduce((groups, item) => {
    const dateKey = new Date(item.datum).toDateString();
    if (!groups[dateKey]) {
      groups[dateKey] = [];
    }
    groups[dateKey].push(item);
    return groups;
  }, {});

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-card shadow-card p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="font-semibold text-text flex items-center gap-2">
          <Clock className="w-5 h-5 text-text-muted" />
          Timeline
        </h3>
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="px-3 py-1.5 border border-border rounded-button bg-surface text-text text-sm"
        >
          <option value="alle">Alle Aktivitäten</option>
          <option value="termin">Termine</option>
          <option value="meilenstein">Meilensteine</option>
          <option value="zeiterfassung">Zeiterfassungen</option>
          <option value="rechnung">Rechnungen</option>
          <option value="kva">KVAs</option>
          <option value="foto">Fotos</option>
          <option value="dokument">Dokumente</option>
        </select>
      </div>

      {filteredTimeline.length === 0 ? (
        <p className="text-text-muted text-center py-8">Keine Aktivitäten</p>
      ) : (
        <div className="space-y-6">
          {Object.entries(groupedByDate).map(([dateKey, items]) => (
            <div key={dateKey}>
              <div className="text-sm font-medium text-text-muted mb-3 sticky top-0 bg-surface py-1">
                {formatDate(new Date(dateKey))}
              </div>
              <div className="relative pl-8 space-y-4">
                {/* Vertikale Linie */}
                <div className="absolute left-3 top-2 bottom-2 w-0.5 bg-border"></div>

                {items.map((item, idx) => {
                  const Icon = ICON_MAP[item.typ] || FileText;
                  const colorClass = COLOR_MAP[item.typ] || 'bg-gray-100 text-gray-600';

                  return (
                    <div key={`${item.typ}-${item.id || idx}`} className="relative">
                      {/* Icon Punkt */}
                      <div className={`absolute -left-5 w-6 h-6 rounded-full flex items-center justify-center ${colorClass}`}>
                        <Icon className="w-3 h-3" />
                      </div>

                      {/* Content */}
                      <div className="bg-surface-elevated rounded-button p-3">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-text text-sm">{item.titel}</p>
                            {item.details && (
                              <p className="text-text-muted text-xs mt-0.5">{item.details}</p>
                            )}
                            {item.benutzer && (
                              <p className="text-text-muted text-xs mt-0.5 flex items-center gap-1">
                                <User className="w-3 h-3" /> {item.benutzer}
                              </p>
                            )}
                          </div>
                          <div className="text-right">
                            <span className="text-xs text-text-muted">{formatTime(item.datum)}</span>
                            {item.status && (
                              <span className={`block text-xs mt-0.5 px-2 py-0.5 rounded-full ${
                                item.status === 'ERLEDIGT' || item.status === 'BEZAHLT' || item.status === 'ANGENOMMEN'
                                  ? 'bg-green-100 text-green-700'
                                  : item.status === 'OFFEN' || item.status === 'NEU'
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-700'
                              }`}>
                                {item.status}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
